// app/(auth)/forgot-password/page.tsx
'use client'

import { useState } from 'react'
import { createClient } from '@/utils/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { Loader2, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const supabase = createClient()

  // In your app/(auth)/forgot-password/page.tsx file
// Replace the existing handleResetPassword function with this:

const handleResetPassword = async (e: React.FormEvent) => {
  e.preventDefault()
  setLoading(true)
  setError('')
  setMessage('')

  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      // CHANGED: Use auth callback instead of direct reset-password URL
      redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
    })

    if (error) {
      setError(error.message)
    } else {
      setMessage('Check your email for a password reset link. It may take a few minutes to arrive.')
    }
  } catch (err) {
    setError('An unexpected error occurred')
  } finally {
    setLoading(false)
  }
}

  return (
    <div className="min-h-screen flex items-center justify-center" 
         style={{
           backgroundImage: `url('https://sbihbgnffzlizvcmzncf.supabase.co/storage/v1/object/public/internal-assets//Smart_Scaling_Community_Login_Background_Image.webp')`,
           backgroundSize: 'cover',
           backgroundPosition: 'center',
           backgroundRepeat: 'no-repeat'
         }}>
      
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
        <div className="absolute bottom-1/4 right-10 w-24 h-24 bg-blue-300/10 rounded-full blur-lg"></div>
        <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-white/5 rounded-full blur-md"></div>
      </div>

      <Card className="w-full max-w-md p-8 shadow-2xl bg-white/95 backdrop-blur-sm border-0 relative z-10">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-xl">⚡</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Forgot Password?</h1>
          <p className="text-gray-600">No worries! Enter your email and we'll send you a reset link.</p>
        </div>

<form 
  onSubmit={handleResetPassword} 
  className="space-y-5"
  suppressHydrationWarning={true}
>
  <div>
    <Label htmlFor="email" className="text-sm font-medium text-gray-700">
      Email Address
    </Label>
    <Input
      id="email"
      type="email"
      placeholder="Enter your email address"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      required
      className="mt-1 h-11 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
    />
  </div>

          {/* Success Message */}
          {message && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              {message}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Send Reset Link Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending Reset Link...
              </div>
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </form>

        {/* Back to Sign In */}
        <div className="text-center pt-6 border-t border-gray-200 mt-6">
          <a
            href="/sign-in"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </a>
        </div>
      </Card>
    </div>
  )
}